import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Snackbar,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { fileService, FileInfo } from '../services/fileService';
import { excelApi } from '../services/api/excelApi';
import { apiClient } from '../services/api/authApi';
import { AccountType } from '../types/auth';
import LicenseeFilter from '../components/common/LicenseeFilter';
import PageHeader from '../components/common/PageHeader';
import { getUserTypeLabel } from './_shared/fileUtils';
import {
  FilesTableContainer,
  FilesTablePagination,
  FilesSearchFilterCard,
  DeleteConfirmDialog,
  useEmailDialogState,
  makeHandleSendEmail,
  ConnectedEmailNotificationDialog,
} from './_shared/FilesTableShared';

const FinalFilesPage: React.FC = () => {
  const { user } = useAuthStore();
  const [finalFiles, setFinalFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const emailState = useEmailDialogState();

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFinalFile, setSelectedFinalFile] = useState<File | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadCompanyId, setUploadCompanyId] = useState<number | null>(null);

  const [filterCompanyId, setFilterCompanyId] = useState<number | ''>('');
  const [filterCompanyName, setFilterCompanyName] = useState<string>('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const isMasterAdmin = user?.accountType === AccountType.MasterAdmin || user?.accountType === AccountType.CarbonCareAdmin;
  const canUpload = !!user?.accountType && user.accountType >= AccountType.CarbonCareAdmin;
  // Final files are only deletable by CarbonCareAdmin+ (revoked for User & Licensee Admin).
  const canDeleteFinal = !!user?.accountType && user.accountType >= AccountType.CarbonCareAdmin;
  // Only the calculations team announces a final file to the licensee.
  const canSendEmail = !!user?.accountType && user.accountType >= AccountType.CarbonCareAdmin;

  const fetchFinalFiles = async (pg = page, lim = rowsPerPage, searchVal = search) => {
    try {
      setLoading(true);
      const result = await fileService.getFinalFiles({
        page: pg + 1,
        limit: lim,
        search: searchVal || undefined,
        companyName: filterCompanyName || undefined,
      });
      setFinalFiles(result.data);
      setTotal(result.pagination.total);
    } catch (err) {
      console.error('Error fetching final files:', err);
      setError('Failed to load final files');
    } finally {
      setLoading(false);
    }
  };

  const prevSearch = React.useRef(search);
  const prevCompany = React.useRef(filterCompanyName);

  useEffect(() => {
    const searchChanged = prevSearch.current !== search;
    const companyChanged = prevCompany.current !== filterCompanyName;
    prevSearch.current = search;
    prevCompany.current = filterCompanyName;

    if (searchChanged) {
      const timer = setTimeout(() => {
        setPage(0);
        fetchFinalFiles(0, rowsPerPage, search);
      }, 600);
      return () => clearTimeout(timer);
    }

    if (companyChanged) {
      setPage(0);
      fetchFinalFiles(0, rowsPerPage, search);
      return;
    }

    fetchFinalFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, rowsPerPage, search, filterCompanyName]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFinalFile(file);
    event.target.value = '';

    if (isMasterAdmin) {
      setUploadDialogOpen(true);
    } else {
      uploadFinalFile(file, user?.companyId || undefined);
    }
  };

  const uploadFinalFile = async (file: File, companyId?: number) => {
    setUploadDialogOpen(false);
    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => (prev >= 90 ? 90 : prev + 10));
      }, 100);

      await excelApi.uploadFile(file, { status: 'final', companyId });

      clearInterval(progressInterval);
      setUploadProgress(100);

      await fetchFinalFiles();

      setTimeout(() => setUploadProgress(0), 500);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to upload file');
      setUploadProgress(0);
    } finally {
      setUploading(false);
      setSelectedFinalFile(null);
      setUploadCompanyId(null);
    }
  };

  const handleDelete = async (file: FileInfo) => {
    try {
      await fileService.deleteFile(file.id);
      setDeleteDialogOpen(false);
      setSelectedFile(null);
      fetchFinalFiles();
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Failed to delete file');
    }
  };

  const handleSendEmail = makeHandleSendEmail({
    fileId: selectedFile?.id,
    customEmails: emailState.customEmails,
    emailComment: emailState.emailComment,
    selectedUserIds: emailState.selectedUserIds,
    setSendingEmail: emailState.setSendingEmail,
    resetEmailDialog: emailState.resetEmailDialog,
    setError,
  });

  const openEmailDialog = async (file: FileInfo) => {
    emailState.setEmailLoadingFileId(file.id);
    setSelectedFile(file);
    try {
      await apiClient.get(`/companies/${file.companyId}`);
      const usersResponse = await apiClient.get(`/companies/${file.companyId}/users`);
      if (usersResponse.data.success) {
        emailState.setCompanyUsers(usersResponse.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      emailState.setCompanyUsers([]);
    } finally {
      emailState.setEmailLoadingFileId(null);
    }
    emailState.setEmailDialogOpen(true);
  };

  return (
    <Box>
      <PageHeader description="Files manually uploaded as final/canonical results for a licensee." />

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 99999 }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Filters */}
      <FilesSearchFilterCard
        searchValue={search}
        onSearchChange={setSearch}
        isMasterAdmin={isMasterAdmin}
        filterCompanyId={filterCompanyId}
        onFilterCompanyChange={(id, name) => { setFilterCompanyId(id); setFilterCompanyName(name); }}
        total={total}
        extraItems={canUpload ? (
          <Grid item xs="auto" sx={{ ml: 'auto' }}>
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
              component="label"
              size="small"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload File'}
              <input
                type="file"
                hidden
                onChange={handleFileSelect}
                accept=".xlsx,.xls,.csv"
              />
            </Button>
          </Grid>
        ) : undefined}
      />

      {uploading && uploadProgress > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {uploadProgress}%
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Uploading file...
          </Typography>
        </Box>
      )}

      <FilesTableContainer loading={loading}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Licensee</TableCell>
              <TableCell>Upload Date</TableCell>
              <TableCell>Uploaded By</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {finalFiles.length > 0 ? (
              finalFiles.map((file) => (
                <TableRow key={file.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {file.originalName}
                    </Typography>
                  </TableCell>
                  <TableCell>{file.companyName}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(file.uploadedAt).toLocaleDateString()} {new Date(file.uploadedAt).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{file?.uploadedBy?.name || 'Unknown'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getUserTypeLabel(file.userType)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => fileService.downloadFile(file.id)}
                      title="Download"
                    >
                      <DownloadIcon />
                    </IconButton>
                    {canSendEmail && (
                      <Tooltip title="Send Email">
                        <IconButton
                          size="small"
                          onClick={() => openEmailDialog(file)}
                          color="secondary"
                          disabled={emailState.emailLoadingFileId === file.id}
                        >
                          {emailState.emailLoadingFileId === file.id ? <CircularProgress size={18} /> : <EmailIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDeleteFinal && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedFile(file);
                            setDeleteDialogOpen(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 4, fontStyle: 'italic' }}
                  >
                    — No files uploaded yet —
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </FilesTableContainer>
      <FilesTablePagination
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => selectedFile && handleDelete(selectedFile)}
        title="Delete File"
        message={`Are you sure you want to delete the file "${selectedFile?.originalName}"? This action cannot be undone.`}
      />

      <ConnectedEmailNotificationDialog
        emailDialogState={emailState}
        fileName={selectedFile?.originalName}
        onSend={handleSendEmail}
      />

      <Dialog
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
          setSelectedFinalFile(null);
          setUploadCompanyId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Licensee</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which licensee this file belongs to.
          </Typography>
          <LicenseeFilter
            value={uploadCompanyId || ''}
            onChange={(id) => setUploadCompanyId(id || null)}
            required
            sx={{ width: '100%' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setSelectedFinalFile(null);
            setUploadCompanyId(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!uploadCompanyId || !selectedFinalFile}
            onClick={() => {
              if (selectedFinalFile && uploadCompanyId) {
                uploadFinalFile(selectedFinalFile, uploadCompanyId);
              }
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinalFilesPage;
